// 따라가는 카메라: 내 카피바라 뒤쪽 위에서 바라본다. 드래그로 돌리고 핀치·휠로 거리 조절.
// 안개 거리, 주광 위치, 가림 디더용 화면 좌표도 여기서 매 프레임 맞춘다.
import * as THREE from 'three';
import { clamp } from './math';
import { occlusion } from './materials';
import type { Lights } from './lights';
import type { Stage } from './renderer';

export const CAMERA = {
  pitchMin: 0.12, pitchMax: 1.3,
  distMin: 7, distMax: 38,
  /** 화면이 세로(비율 < 0.9)일 때와 가로일 때의 기본 거리. */
  distPortrait: 23, distLandscape: 17,
  /** 바라보는 점을 카피바라보다 앞쪽(카메라 반대쪽)으로 미는 거리. */
  lead: 2.5,
};

export interface FollowCamera {
  yaw: number;
  pitch: number;
  dist: number;
  /** 사용자가 직접 줌했으면 화면 비율이 바뀌어도 거리를 유지한다. */
  zoomed: boolean;
  target: THREE.Vector3;
  /** 실내(료칸 식당): 가까이·낮게, 돌리기 제한, 앞쪽 식탁을 바라본다. 안개도 멀리 민다. */
  indoor: boolean;
  /** 찜질방 식탁에서 먹는 중: 내 카피바라 바로 위를 낮게 바라본다 (yaw·pitch·dist는 방이 정한다). */
  dine: boolean;
  orbit(dx: number, dy: number): void;
  zoomTo(dist: number): void;
  /** 다음 프레임에 부드럽게 따라가지 않고 바로 맞춘다. */
  snap(): void;
  /** follow: 따라갈 카피바라 root. */
  update(dt: number, follow: THREE.Object3D): void;
}

export function createFollowCamera(stage: Stage, lights: Lights, start: THREE.Vector3): FollowCamera {
  const { camera, renderer, fog } = stage;
  const host = renderer.domElement.parentElement ?? document.body;
  const tmp = new THREE.Vector3();
  let snapNext = false;

  const cam: FollowCamera = {
    yaw: 0, pitch: 0.32, dist: CAMERA.distLandscape, zoomed: false, indoor: false, dine: false,
    target: new THREE.Vector3(start.x, 0.6, start.z),
    orbit(dx, dy) {
      cam.yaw -= dx * 0.006;
      cam.pitch = clamp(cam.pitch + dy * 0.004, CAMERA.pitchMin, CAMERA.pitchMax);
    },
    zoomTo(d) {
      cam.dist = clamp(d, CAMERA.distMin, CAMERA.distMax);
      cam.zoomed = true;
    },
    snap() { snapNext = true; },
    update(dt, follow) {
      const pr = follow.position;
      let lookX = pr.x - Math.sin(cam.yaw) * CAMERA.lead, lookZ = pr.z - Math.cos(cam.yaw) * CAMERA.lead;
      if (cam.indoor) { cam.yaw = clamp(cam.yaw, -0.6, 0.6); cam.dist = clamp(cam.dist, 4.5, 9); cam.pitch = clamp(cam.pitch, 0.15, 0.36); lookX = pr.x; lookZ = pr.z + 0.8; }
      else if (cam.dine) { lookX = pr.x; lookZ = pr.z; }
      const lookY = 0.6 + Math.max(0, pr.y) * 0.85;
      const t = cam.target;
      if (snapNext) { t.set(lookX, lookY, lookZ); snapNext = false; }
      t.x += (lookX - t.x) * Math.min(1, dt * 2);
      t.z += (lookZ - t.z) * Math.min(1, dt * 2);
      t.y += (lookY - t.y) * Math.min(1, dt * 2.5);
      const cp = Math.cos(cam.pitch);
      camera.position.set(t.x + Math.sin(cam.yaw) * cp * cam.dist, t.y + Math.sin(cam.pitch) * cam.dist, t.z + Math.cos(cam.yaw) * cp * cam.dist);
      camera.lookAt(t.x, t.y + (cam.indoor || cam.dine ? 0.75 : 2.4), t.z);
      if (fog) { fog.near = cam.indoor ? 60 : cam.dist + 2; fog.far = cam.indoor ? 100 : cam.dist + 40; }

      // 가림 디더: 카피바라 가슴 높이의 화면 좌표·거리·반지름
      tmp.set(pr.x, pr.y + 1.3, pr.z);
      const dcam = camera.position.distanceTo(tmp);
      tmp.project(camera);
      const pxr = renderer.getPixelRatio(), H = host.clientHeight, W = host.clientWidth;
      occlusion.scr.value.set((tmp.x + 1) / 2 * W * pxr, (tmp.y + 1) / 2 * H * pxr);
      occlusion.depth.value = dcam;
      occlusion.rad.value = 2.0 / Math.max(1, dcam) * (H / 2) / Math.tan(camera.fov * Math.PI / 360) * pxr;

      lights.follow(t.x, t.z);
    },
  };

  stage.onResize.add((_w, _h, aspect) => {
    if (!cam.zoomed) cam.dist = aspect < 0.9 ? CAMERA.distPortrait : CAMERA.distLandscape;
  });
  return cam;
}
